import axios from 'axios';
import { api } from './api';
import type { PublicTenantDto } from '../types/publicTenant';
import {
  getDevMockHostFromStorage,
  getDevTenantSlugFromStorage,
  hasDevTenantContext,
  isLocalDevHost,
} from './devTenantContext';

const STORAGE_PUBLIC_TENANT = '@KnowInDoc:publicTenantContext';

function readStoredTenant(): PublicTenantDto | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_PUBLIC_TENANT);
    if (!raw) return null;
    return JSON.parse(raw) as PublicTenantDto;
  } catch {
    return null;
  }
}

export function getCachedPublicTenant(): PublicTenantDto | null {
  return readStoredTenant();
}

export function setCachedPublicTenant(dto: PublicTenantDto | null): void {
  if (typeof window === 'undefined') return;
  if (!dto) {
    sessionStorage.removeItem(STORAGE_PUBLIC_TENANT);
    return;
  }
  sessionStorage.setItem(STORAGE_PUBLIC_TENANT, JSON.stringify(dto));
}

export function clearTenantBootstrapContext(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_PUBLIC_TENANT);
}

export function shouldFetchPublicTenantOnBootstrap(): boolean {
  if (!isLocalDevHost()) return true;
  if (hasDevTenantContext()) return true;
  const id = process.env.REACT_APP_API_CLIENT_ID?.trim();
  const secret = process.env.REACT_APP_API_CLIENT_SECRET?.trim();
  if (id && secret) return false;
  return true;
}

export function buildSpaTokenRequestBody():
  | { Host?: string; TenantSlug?: string; Path?: string }
  | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname || '/';

  if (isLocalDevHost()) {
    const mock = getDevMockHostFromStorage();
    const slug = getDevTenantSlugFromStorage();
    if (mock) return { Host: mock, Path: path };
    if (slug) return { TenantSlug: slug, Path: path };
    return { Host: window.location.hostname, Path: path };
  }

  return { Host: window.location.hostname, Path: path };
}

export type TenantResolveDetail =
  | { status: 'ok'; tenant: PublicTenantDto }
  | { status: 'not_found' }
  | { status: 'error' };

export type TenantBootstrapState =
  | { kind: 'loading' }
  | { kind: 'skipped' }
  | { kind: 'success' }
  | { kind: 'not_found' }
  | { kind: 'unavailable' };

export async function fetchPublicTenantContextDetailed(): Promise<TenantResolveDetail> {
  try {
    const { data } = await api.get<PublicTenantDto>('/public/tenant/resolve');
    if (!data?.clientId) return { status: 'error' };
    return { status: 'ok', tenant: data };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return { status: 'not_found' };
    }
    return { status: 'error' };
  }
}

export async function fetchPublicTenantContext(): Promise<PublicTenantDto | null> {
  const r = await fetchPublicTenantContextDetailed();
  return r.status === 'ok' ? r.tenant : null;
}

export async function resolveTenantContextDetailed(
  forceRefresh = false
): Promise<TenantResolveDetail> {
  if (!forceRefresh) {
    const cached = getCachedPublicTenant();
    if (cached?.clientId) return { status: 'ok', tenant: cached };
  }
  const result = await fetchPublicTenantContextDetailed();
  if (result.status === 'ok') {
    setCachedPublicTenant(result.tenant);
  } else {
    setCachedPublicTenant(null);
  }
  return result;
}

export async function resolveTenantContext(
  forceRefresh = false
): Promise<PublicTenantDto | null> {
  const r = await resolveTenantContextDetailed(forceRefresh);
  return r.status === 'ok' ? r.tenant : null;
}
